import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadBlob } from '@/utils/download';
import { LEAVE_REPORT_PAGE_SIZE } from '../constants/leaveReport.constants';
import {
  leaveReportDatesSchema,
  paginatedLeaveReportSchema,
  type LeaveReportDates,
  type LeaveReportParams,
  type LeaveReportPeriod,
  type PaginatedLeaveReport,
} from '../types/leaveReport.types';

export const leaveReportQueryKeys = {
  all: () => ['leave-reports'] as const,
  lists: () => [...leaveReportQueryKeys.all(), 'list'] as const,
  listInfinite: (params: LeaveReportParams) =>
    [...leaveReportQueryKeys.lists(), 'infinite', params] as const,
  dates: (userId: number, period: LeaveReportPeriod) =>
    [...leaveReportQueryKeys.all(), 'dates', period, userId] as const,
};

export const leaveReportListInfiniteQueryOptions = (params: LeaveReportParams) =>
  infiniteQueryOptions({
    queryKey: leaveReportQueryKeys.listInfinite(params),
    queryFn: async ({ pageParam }): Promise<PaginatedLeaveReport> => {
      const res = await apiClient.get(API.leaveReport.list, {
        params: { ...params, page: pageParam, pageSize: LEAVE_REPORT_PAGE_SIZE },
      });
      return paginatedLeaveReportSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const leaveReportDatesQueryOptions = (
  userId: number,
  { dateFrom, dateTo }: LeaveReportPeriod,
) =>
  queryOptions({
    queryKey: leaveReportQueryKeys.dates(userId, { dateFrom, dateTo }),
    queryFn: async (): Promise<LeaveReportDates> => {
      const res = await apiClient.get(API.leaveReport.dates(userId), {
        params: { dateFrom, dateTo },
      });
      return leaveReportDatesSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export async function downloadLeaveReport(params: LeaveReportParams): Promise<void> {
  const res = await apiClient.get<Blob>(API.leaveReport.export, {
    params,
    responseType: 'blob',
  });

  downloadBlob(res.data, `leave-report-${params.dateFrom}_${params.dateTo}.xlsx`);
}
